import React, { useContext, useEffect, useState } from "react";
import CustomerTranslations from "../../utils/CustomerTranslations";
import { isSupportedCountry, isValidPhoneNumber } from "react-phone-number-input/max";
import styled, { useTheme } from "styled-components";
import { ActionType, BackFromDetailsStepPayload, CompleteDetailsStepPayload } from "../../AppDataProvider/actions";
import { AppContext, BookingData, CrmFieldData, CrmFieldValue, UserDetails } from "../../AppDataProvider/AppDataProvider";
import { LoadingPill as BaseLoadingPill, Input, InputElement, LoadingSpinner, NavButtons } from "../../common/components";
import { marginBottom, marginLeft, typography } from "../../common/styles";
import { BookingVisibilityType, PaymentOption, useCreateBookingsForBookingToolMutation, useGetOrganisationPaymentQuery } from "../../GeneratedGraphQL";
import { getBookingQuestionInput, getCrmFieldBookingInput, getDetailsPreviousStep, getPaymentIntegrationType, requireBookingDataFields } from "../../utils";
import { useApolloClient } from "@apollo/client";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { E164PhoneNumber } from "aws-sdk/clients/chime";
import { useTranslation } from "react-i18next";
import { RequiredStar } from "../../common/components/Input/common";
import { absoluteCentre } from "../../common/styles";
import { Mixpanel } from "../../mixpanel";
import { emitStepEvent } from "../../utils/emitEvent/emitStepEvent";
import { PartRequired } from "../../utils/types";
import { useDeleteExistingReservedSpace } from "../Availability/hooks/useDeleteExistingReservedSpace";
import CrmFields from "./CrmFields/CrmFields";
import { parsePrefillData } from "./parsePrefillData/parsePrefillData";
const VALID_TEXT_MAX_LENGTH = 200;
type Errors = {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
};
type DetailsStepBookingData = PartRequired<BookingData, "createdSource" | "organisation" | "service" | "timezone">;
export function DetailsStep() {
  const client = useApolloClient();
  const theme = useTheme();
  const {
    translateEntity
  } = CustomerTranslations.useCustomerTranslation();
  const {
    t,
    i18n
  } = useTranslation();
  // Ignore lack of test coverage for locale "cimode" as it will only have "cimode" when in test mode
  /* istanbul ignore next */
  const locale = i18n.language !== "cimode" ? i18n.language : "en-GB";
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  requireBookingDataFields<DetailsStepBookingData>(appData.bookingData, ["createdSource", "organisation", "service", "timezone"]);
  const {
    bookingData,
    bookingTool,
    userLocation
  } = appData;
  const {
    forcePhoneValidationToCountry
  } = bookingTool;
  const {
    bookingNotes,
    bookingQuestionData,
    createdSource,
    organisation,
    pricePoints,
    reservedSpaceId,
    selectedBookings,
    service,
    tier,
    timezone,
    userDetails
  } = bookingData;
  const deleteExistingReservedSpace = useDeleteExistingReservedSpace({
    organisationId: organisation._id,
    reservedSpaceId
  });
  let defaultPhoneInputCountry = undefined;
  if (forcePhoneValidationToCountry) {
    defaultPhoneInputCountry = forcePhoneValidationToCountry;
  } else if (userLocation && isSupportedCountry(userLocation)) {
    defaultPhoneInputCountry = userLocation;
  }
  const {
    enableMarketingOptIn,
    externalCrmFields,
    isPhoneNumberRequiredForBookings,
    privacyPolicyUrl,
    marketingEnabled
  } = organisation;
  const prefillData = parsePrefillData();
  const defaultUserDetails: UserDetails = {
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    ...prefillData,
    // Marketing opt in should never be prefilled.
    marketingOptIn: false,
    ...userDetails
  };
  const [firstName, setFirstName] = useState<string>(defaultUserDetails.firstName);
  const [lastName, setLastName] = useState<string>(defaultUserDetails.lastName);
  const [email, setEmail] = useState<string>(defaultUserDetails.email);
  const [phone, setPhone] = useState<string>(defaultUserDetails.phone);
  const [marketingOptIn, setMarketingOptIn] = useState<boolean>(defaultUserDetails.marketingOptIn ?? false);
  const [errors, setErrors] = useState<Errors>({});
  const [crmFieldData, setCrmFieldData] = useState<CrmFieldData | undefined>(defaultUserDetails.crmFieldData);
  const [crmFieldErrors, setCrmFieldErrors] = useState<Record<string, string>>({});
  const [isCreatingBooking, setIsCreatingBooking] = useState<boolean>(false);
  const [deleteReservationLoading, setDeleteReservationLoading] = useState<boolean>(false);
  const [navigationDisabled, setNavigationDisabled] = useState(false);
  const [createBookingsForBookingTool] = useCreateBookingsForBookingToolMutation({
    errorPolicy: "all"
  });
  const {
    data,
    loading,
    error,
    refetch
  } = useGetOrganisationPaymentQuery({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    skip: !reservedSpaceId,
    variables: {
      bookingReservations: [{
        /**
         * reservedSpaceId might be undefined (when the reservation is deleted)
         * therefore we need to skip the query if it's undefined
         * however the type of reservedSpaceId is a string so we need to pass
         * in an empty string to satisfy TS as it's not clever enough to know
         * to ignore the typing when the query is being skipped.
         */
        reservedSpaceId: reservedSpaceId ?? "",
        timezone: timezone,
        ...(selectedBookings && {
          bookingIds: selectedBookings.map(booking => booking._id)
        }),
        ...(tier && {
          tierId: tier._id
        }),
        ...(pricePoints && {
          pricePoints: pricePoints.map(pricePoint => {
            return {
              pricePointId: pricePoint.pricePoint._id,
              quantity: pricePoint.quantity
            };
          })
        })
      }],
      id: organisation._id,
      locale
    }
  });
  useEffect(() => {
    emitStepEvent(appData, "CUSTOMER_DETAILS_STEP");

    /**
     * We disable this eslint rule as the hook is only meant to run when the
     * component is mounted and not on changes to the appData object.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // This function really doesn't need to/can't be sensibly tested because it's
    // just calling the refetch function from apollo client.
    /* istanbul ignore next */
    const retryPaymentQuery = () => void refetch();
    /* istanbul ignore next */
    const reload = () => location.reload();
    if (!loading) {
      if (error) {
        const dataHasChanged = error.graphQLErrors.some(error => error.extensions?.code === "BAD_USER_INPUT");
        console.error({
          error
        });
        appDataDispatch({
          payload: {
            buttonLabel: dataHasChanged ? t("reload") : t("tryAgain"),
            content: dataHasChanged ? t("errors.somethingChanged") : t("errors.paymentError"),
            icon: faExclamationTriangle,
            onClick: dataHasChanged ? reload : retryPaymentQuery,
            title: dataHasChanged ? t("somethingChanged") : t("serverError")
          },
          type: ActionType.SHOW_MODAL_DIALOG
        });
      } else if (reservedSpaceId && (!data || !data.organisation?.payment)) {
        appDataDispatch({
          payload: {
            buttonLabel: t("tryAgain"),
            content: t("errors.generic"),
            icon: faExclamationTriangle,
            onClick: retryPaymentQuery,
            title: t("serverError")
          },
          type: ActionType.SHOW_MODAL_DIALOG
        });
      }
    }
  }, [appDataDispatch, data, error, loading, refetch, reservedSpaceId, t]);
  if (loading || deleteReservationLoading || !reservedSpaceId) return <LoadingPill />;
  if (error || !data || !data.organisation?.payment) return null;
  const paymentPriceInPence = data.organisation.payment.paymentPriceInPence;
  const clearError = (fieldName: keyof Errors) => {
    if (errors[fieldName]) {
      const newErrors = {
        ...errors
      };
      delete newErrors[fieldName];
      setErrors(newErrors);
    }
  };
  const onFirstNameChange = (event: React.ChangeEvent<InputElement>) => {
    const newValue = event.target.value;
    setFirstName(newValue);
    clearError("firstName");
  };
  const onLastNameChange = (event: React.ChangeEvent<InputElement>) => {
    const newValue = event.target.value;
    setLastName(newValue);
    clearError("lastName");
  };
  const onEmailChange = (event: React.ChangeEvent<InputElement>) => {
    const newValue = event.target.value;
    setEmail(newValue);
    clearError("email");
  };
  const onPhoneChange = (newValue: E164PhoneNumber) => {
    setPhone(newValue);
    clearError("phone");
  };
  const onCrmFieldChange = (crmFieldId: string, newValue: CrmFieldValue) => {
    setCrmFieldData({
      ...crmFieldData,
      [crmFieldId]: newValue
    });
    if (crmFieldErrors[crmFieldId]) {
      const newCrmFieldErrors = {
        ...crmFieldErrors
      };
      delete newCrmFieldErrors[crmFieldId];
      setCrmFieldErrors(newCrmFieldErrors);
    }
  };
  const onMarketingOptInChange = () => {
    setMarketingOptIn(val => !val);
  };
  const isPreviousStepAvailability = getDetailsPreviousStep({
    bookingData,
    bookingTool
  }) === "availability" || getDetailsPreviousStep({
    bookingData,
    bookingTool
  }) === "locationAvailability";
  const goBack = () => {
    if (isCreatingBooking) return;
    const goBackCleanup = async () => {
      if (isPreviousStepAvailability && reservedSpaceId) {
        setDeleteReservationLoading(true);
        await deleteExistingReservedSpace();
        setDeleteReservationLoading(false);
      }
      await client.clearStore();
      const payload: BackFromDetailsStepPayload = {
        updatedData: {
          userDetails: {
            email,
            firstName,
            lastName,
            marketingOptIn,
            phone,
            ...(crmFieldData && {
              crmFieldData
            })
          }
        }
      };
      appDataDispatch({
        payload,
        type: ActionType.BACK_FROM_DETAILS_STEP
      });
    };
    void goBackCleanup();
  };
  const isInputValid = () => {
    const newErrors: Errors = {};
    const newCrmFieldErrors: Record<string, string> = {};
    if (!firstName) newErrors.firstName = t("errors.required");
    if (firstName.length > VALID_TEXT_MAX_LENGTH) newErrors.firstName = t("errors.textMaxCharacters", {
      max: VALID_TEXT_MAX_LENGTH
    });
    if (!lastName) newErrors.lastName = t("errors.required");
    if (lastName.length > VALID_TEXT_MAX_LENGTH) newErrors.lastName = t("errors.textMaxCharacters", {
      max: VALID_TEXT_MAX_LENGTH
    });
    if (!phone && isPhoneNumberRequiredForBookings) newErrors.phone = t("errors.required");

    /**
     * FIXME: There is a scenario where phone can be undefined, despite the type suggesting otherwise, leading to a crash.
     * https://appointedd-eu.sentry.io/issues/1387942/events/6ef17d7436714534b2e35fd63c456d54/
     *
     * For now just adding an additional check for truthyness to avoid this.
     */
    if (phone && phone.length > 0 && !isValidPhoneNumber(phone)) {
      newErrors.phone = t("errors.invalidPhone");
    }
    if (email.length > VALID_TEXT_MAX_LENGTH) newErrors.email = t("errors.textMaxCharacters", {
      max: VALID_TEXT_MAX_LENGTH
    });

    // regex for email validation comes from here (ajv v6.7.0) - https://github.com/ajv-validator/ajv/blob/38a9ad4cf9498532190f49fac7245930e649fe58/lib/compile/formats.js#L72
    const emailPattern = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;
    if (email && !email.match(emailPattern)) {
      newErrors.email = t("errors.invalidEmail");
    } else if (!email) {
      newErrors.email = t("errors.required");
    }
    externalCrmFields.forEach(({
      id,
      required
    }) => {
      const value = crmFieldData?.[id];
      if (required && !value) newCrmFieldErrors[id] = t("errors.required");
    });
    const combinedErrors = {
      ...newErrors,
      ...newCrmFieldErrors
    };
    const hasErrors = Object.values(combinedErrors).length > 0;
    if (hasErrors) {
      Mixpanel.track({
        name: "Form validation error",
        props: {
          errors: combinedErrors,
          step: "details"
        }
      });
    }
    setErrors(newErrors);
    setCrmFieldErrors(newCrmFieldErrors);
    return !hasErrors;
  };
  const completeStep = () => {
    const payload: CompleteDetailsStepPayload = {
      updatedData: {
        userDetails: {
          email,
          firstName,
          lastName,
          marketingOptIn,
          phone,
          ...(crmFieldData && {
            crmFieldData
          })
        }
      }
    };
    appDataDispatch({
      payload,
      type: ActionType.COMPLETE_DETAILS_STEP
    });
  };
  const confirmBooking = async () => {
    if (!isInputValid()) return;
    setIsCreatingBooking(true);
    setNavigationDisabled(true);
    const {
      errors,
      data
    } = await createBookingsForBookingTool({
      variables: {
        bookings: [{
          customer: {
            crmFieldData: getCrmFieldBookingInput(crmFieldData),
            email,
            firstName,
            lastName,
            marketingOptIn,
            phone
          },
          organisationId: organisation._id,
          reservedSpaceId: reservedSpaceId,
          timezone: timezone,
          ...(selectedBookings && {
            bookingIds: selectedBookings.map(booking => booking._id)
          }),
          ...(tier && {
            tierId: tier._id
          }),
          ...(pricePoints && {
            pricePoints: pricePoints.map(pricePoint => {
              return {
                pricePointId: pricePoint.pricePoint._id,
                quantity: pricePoint.quantity
              };
            })
          }),
          ...(bookingNotes && {
            bookingNotes
          }),
          bookingQuestions: getBookingQuestionInput({
            bookingQuestionData,
            bookingQuestions: organisation.externalBookingQuestions,
            pickedServiceId: service._id
          })
        }],
        locale,
        ...(!appData.bookingTool.isGenericBookingTool && {
          bookingToolId: appData.bookingTool._id
        }),
        createdSource
      }
    });
    if (errors) {
      if (errors.some(error => error.extensions?.code === "BAD_USER_INPUT")) {
        appDataDispatch({
          payload: {
            buttonLabel: t("reload"),
            content: t("errors.somethingChanged"),
            icon: faExclamationTriangle,
            onClick: /* istanbul ignore next */() => location.reload(),
            title: t("somethingChanged")
          },
          type: ActionType.SHOW_MODAL_DIALOG
        });
      }
      appDataDispatch({
        payload: {
          message: t("errors.cannotConfirmBooking"),
          variant: "warning"
        },
        type: ActionType.ADD_TOAST
      });
    } else {
      const payload: CompleteDetailsStepPayload = {
        updatedData: {
          confirmedBookingIds: data?.createBookingsForBookingTool.bookings.map(booking => booking._id),
          userDetails: {
            email,
            firstName,
            lastName,
            marketingOptIn,
            phone,
            ...(crmFieldData && {
              crmFieldData
            })
          }
        }
      };
      appDataDispatch({
        payload,
        type: ActionType.COMPLETE_DETAILS_STEP
      });
    }
    setIsCreatingBooking(false);
    setNavigationDisabled(false);
  };
  const confirmDetails = () => {
    if (isInputValid()) completeStep();
  };
  const paymentsIntegrationType = getPaymentIntegrationType(organisation);
  const paymentEnabled = paymentsIntegrationType !== null && service.bookingPaymentOption !== PaymentOption.None && paymentPriceInPence > 0;
  const continueLabel = service.bookingVisibilityType === BookingVisibilityType.Request ? t("requestNow") : t("bookNow");
  let marketingOptInLabel = organisation.marketingOptInLabel?.trim().length ? organisation.marketingOptInLabel : t("marketingOptInLabel", {
    organisationName: translateEntity({
      document: organisation,
      type: "account"
    }).name
  });
  let privacyPolicyContent;

  /**
   * Use localised custom content for the marketing opt-in label if the
   * organisation has one set.
   */
  if (organisation.customContent) {
    const currentLocale = i18n.resolvedLanguage ?? i18n.language;
    const localisedCustomContent = organisation.customContent.find(entry => entry.language === currentLocale);
    if (localisedCustomContent?.marketingOptInLabel) {
      marketingOptInLabel = localisedCustomContent.marketingOptInLabel;
    }
    if (localisedCustomContent?.privacyPolicy) {
      privacyPolicyContent = localisedCustomContent.privacyPolicy;
    }
  }
  return <>
      <FormTitle data-sentry-element="FormTitle" data-sentry-source-file="DetailsStep.tsx">{t("yourDetails")}</FormTitle>
      <Form id="detailsForm" aria-label={t("userDetailsForm")} data-sentry-element="Form" data-sentry-source-file="DetailsStep.tsx">
        <Input variant="text" id={"firstName"} label={t("firstName")} error={errors.firstName} required value={firstName} onChange={onFirstNameChange} autoComplete="given-name" data-sentry-element="Input" data-sentry-source-file="DetailsStep.tsx" />

        <Input variant="text" id={"lastName"} label={t("lastName")} error={errors.lastName} required value={lastName} onChange={onLastNameChange} autoComplete="family-name" data-sentry-element="Input" data-sentry-source-file="DetailsStep.tsx" />

        <Input variant="email" id={"email"} label={t("email")} error={errors.email} required value={email} onChange={onEmailChange} autoComplete="email" data-sentry-element="Input" data-sentry-source-file="DetailsStep.tsx" />

        <Input variant="phone" id={"phoneNumber"} label={t("phoneNumber")} error={errors.phone} required={isPhoneNumberRequiredForBookings} defaultCountry={defaultPhoneInputCountry} value={phone} onChange={onPhoneChange} data-sentry-element="Input" data-sentry-source-file="DetailsStep.tsx" />

        <CrmFields crmFields={externalCrmFields} crmFieldData={crmFieldData ?? {}} errors={crmFieldErrors} onCrmFieldValueChange={onCrmFieldChange} data-sentry-element="CrmFields" data-sentry-source-file="DetailsStep.tsx" />

        {(marketingEnabled || enableMarketingOptIn) && <Input variant="checkbox" id={"marketingOptIn"} label={marketingOptInLabel} checked={marketingOptIn ?? false} onChange={onMarketingOptInChange} />}
      </Form>

      {!privacyPolicyContent && privacyPolicyUrl && <PrivacyPolicyTextContainer>
          {t("reviewOur")}{" "}
          <a href={privacyPolicyUrl} target="blank">
            {t("privacyPolicyHere")}
          </a>
        </PrivacyPolicyTextContainer>}

      {privacyPolicyContent && <PrivacyPolicyTextContainer dangerouslySetInnerHTML={{
      __html: privacyPolicyContent
    }} />}

      <RequiredFields data-sentry-element="RequiredFields" data-sentry-source-file="DetailsStep.tsx">
        <RequiredStar data-sentry-element="RequiredStar" data-sentry-source-file="DetailsStep.tsx" />
        <RequiredFieldsText data-sentry-element="RequiredFieldsText" data-sentry-source-file="DetailsStep.tsx">{t("requiredFields")}</RequiredFieldsText>
      </RequiredFields>

      <NavButtons {...{
      onBack: goBack,
      onContinue: paymentEnabled ? confirmDetails : () => {
        void (async () => {
          await confirmBooking();
        })();
      },
      ...(!paymentEnabled && {
        continueLabel
      }),
      ...(isCreatingBooking && {
        continueLabel: <LoadingSpinner colour={theme.colours.textOnPrimary} />
      }),
      navigationDisabled: navigationDisabled
    }} data-sentry-element="NavButtons" data-sentry-source-file="DetailsStep.tsx" />
    </>;
}
export default DetailsStep;
const LoadingPill = styled(BaseLoadingPill)`
  ${absoluteCentre}
`;
const FormTitle = styled.h2`
  &::first-letter {
    text-transform: capitalize;
  }
  ${typography.bodyBold}
  ${marginBottom.m}
  /*
   * Was unset — this step's content sits directly in ContentContainer's
   * BodyContainer, which (deliberately, per ContentContainer.tsx) never
   * sets an ambient text colour, so this fell back to the browser default
   * black and was invisible on dark-theme brands. Same for
   * PrivacyPolicyTextContainer/RequiredFieldsText below.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const Form = styled.form`
  display: flex;
  flex-direction: column;
`;
const PrivacyPolicyTextContainer = styled.div`
  ${marginBottom.m}
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const RequiredFields = styled.div`
  display: flex;
  align-items: flex-start;
`;
const RequiredFieldsText = styled.div`
  ${typography.small}
  ${marginLeft.xs}
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;